import React, { useState, useMemo } from 'react'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { TPlan } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'

import { BeforePlanSubscriptionC } from '@/components/beforePlanSubscription/typeC'
import { CompanyContacts } from '@/components/companyContacts'
import { PageTitle } from '@/components/pageTitle'
import { PaymentMethodSwitcher } from '@/components/paymentMethodSwitcher'
import { Receipt } from '@/components/receipt'
import { TReceiptData } from '@/components/receipt/types'
import { InvertedShield } from '@/shared/icons/InvertedShield'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'

import { paypalButtonMap, stripeCheckoutFormMap, walletPaymentButtonMap } from './componentMappings'
import { useProcessContactState } from './hooks/contactState/useProcessContactState'
import useTrackPageAndPaymentScreen from './hooks/trackWebEvents/useTrackWebEvents'
import { TSinglePaymentScreenPageProps } from './types'

const paypalOptions = getDefaultPayPalOptions()

const pageName = PageName.paymentScreen

function getReceiptData(planData: TPlan): TReceiptData | undefined {
  const plan = planData
  if (!plan.crossedPrice) {
    return
  }

  const discountAmount = plan.currencySign + (plan.crossedPrice - plan.trialPrice).toFixed(2)
  return {
    priceInfo: [
      {
        textPlan: plan.orderPlanName,
        textPrice: plan.currencySign + plan.crossedPrice?.toFixed(2),
      },
      {
        textPlan: 'Discount -30%',
        textPrice: discountAmount,
        extraClassName: 'discount',
        priceExtraClassName: 'highlight',
      },
    ],
    totalPrice: {
      textPlan: 'Total',
      textPrice: plan.currencySign + plan.trialPrice?.toFixed(2),
      weekTextPrice: plan.periodPrice ? `You just saved ${discountAmount} (30% off)` : '',
    },
  }
}
export const SinglePaymentScreenPage = ({
  isAdditional = false,
  getItem,
  setItem,
  pageConfig,
  config,
  stripePromise,
  stripeFormAppearance,
  paypalButtonConfigStyle,
  planData,
  getStripeWalletButtonStyles,
  successRedirectTo = PageName.paymentSuccess,
  contactStateRoutesMap,
}: TSinglePaymentScreenPageProps) => {
  const { checkoutFlowVersion } = useSession()
  const {
    discount,
    title,
    pageTitle,
    paymentMethodTitle,
    receiptTitle,
    stripeFormConfig,
    walletButtonConfig,
    paypalCustomTagline,
    showGuaranteesLabel,
    showCompanyContacts,
  } = pageConfig

  const showPayPal = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
    [config]
  )
  const showStripe = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
    [config]
  )

  const { user } = useAuth()
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | undefined>(
    showPayPal ? PaymentMethod.PAYPAL : PaymentMethod.CARD
  )

  const className = `page page-${pageName}`

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getDefaultStripeOptions(planData, stripeFormAppearance),
    [planData, stripeFormAppearance]
  )

  useTrackPageAndPaymentScreen(config, getItem, setItem, pageTitle, planData, pageName)

  const contactStateEl = useProcessContactState(
    config.PAGE_SET_ID,
    getItem,
    setItem,
    { className, stateRoutes: contactStateRoutesMap },
    checkoutFlowVersion
  )
  if (contactStateEl) {
    return contactStateEl
  }

  if (!planData || !user) {
    console.log('no planData')
    return null
  }

  const receiptData = getReceiptData(planData)

  const changePaymentMethod = (type: PaymentMethod) => {
    setCurrentPaymentMethod(type)
  }

  if (!stripeOptions) {
    return null
  }

  const WalletPaymentFC = walletPaymentButtonMap[checkoutFlowVersion]
  const PayPalButtonFC = paypalButtonMap[checkoutFlowVersion]
  const StripeCheckoutFormFC = stripeCheckoutFormMap[checkoutFlowVersion]

  return (
    <div className={className}>
      <PayPalScriptProvider options={{ ...paypalOptions }}>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <div className="offer">{title}</div>
          <PageTitle title={discount} />
          <div className="payment-method">{paymentMethodTitle}</div>
          <div className={'floating-container ' + (isAdditional ? 'extended' : '')}>
            <PaymentMethodSwitcher
              onChange={changePaymentMethod}
              currentPaymentMethod={currentPaymentMethod}
              showStripe={showStripe}
              showPayPal={showPayPal}
            />

            {showStripe && (!walletButtonConfig || !walletButtonConfig.underForm) && (
              <WalletPaymentFC
                planData={planData}
                getItem={getItem}
                setItem={setItem}
                pageName={pageName}
                config={config}
                stripeButtonTheme={StripeWalletButtonTheme.dark}
                getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}

            {receiptData && <Receipt receiptData={receiptData} receiptTitle={receiptTitle} />}

            {showPayPal && currentPaymentMethod === PaymentMethod.PAYPAL && (
              <div className="paypal-container">
                <PayPalButtonFC
                  pageName={pageName}
                  paypalButtonConfigStyle={paypalButtonConfigStyle}
                  productData={planData}
                  getItem={getItem}
                  setItem={setItem}
                  config={config}
                  customTagline={paypalCustomTagline}
                  successRedirectTo={successRedirectTo}
                  contactStateRoutesMap={contactStateRoutesMap}
                />
              </div>
            )}
            {showStripe && currentPaymentMethod === PaymentMethod.CARD && (
              <StripeCheckoutFormFC
                pageName={pageName}
                getItem={getItem}
                setItem={setItem}
                planDetails={planData}
                config={config}
                formConfig={stripeFormConfig}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}
            {showStripe && walletButtonConfig && walletButtonConfig.underForm && (
              <WalletPaymentFC
                planData={planData}
                getItem={getItem}
                setItem={setItem}
                pageName={pageName}
                config={config}
                stripeButtonTheme={StripeWalletButtonTheme.dark}
                getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                showDelimiter={walletButtonConfig.showDelimiter}
                buttonsDelimiterText={walletButtonConfig.delimiterText}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}

            {!isAdditional && <BeforePlanSubscriptionC selectedPlan={planData} />}

            {showGuaranteesLabel && (
              <div className="guarantees-container">
                <InvertedShield />
                Guaranteed security payments
              </div>
            )}
            {showCompanyContacts && <CompanyContacts />}
          </div>
        </Elements>
      </PayPalScriptProvider>
    </div>
  )
}
